import { fetchUtils, HttpError } from 'react-admin';
import { stringify } from 'query-string';
import { convertFileToBase64 } from '../helpers';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

const normalizeParams = async (resource, params) => {
  if (resource === 'artists') {
    const photo = params.data.pictures
      ? await convertFileToBase64(params.data.pictures.rawFile)
      : (params.previousData && params.previousData.photo) || null;
    return { ...params.data, photo };
  } else if (resource === 'categories') {
    const preview_image_url = params.data.pictures
      ? await convertFileToBase64(params.data.pictures.rawFile)
      : (params.previousData && params.previousData.preview_image_url) || null;

    return { ...params.data, preview_image_url };
  }
  return params.data;
};

export default () => {
  const httpClient = (url, options = {}) => {
    const clientOptions = { ...options };

    // Ensure headers exist
    clientOptions.headers = clientOptions.headers instanceof Headers
      ? clientOptions.headers
      : new Headers(clientOptions.headers || {});

    // Default headers
    clientOptions.headers.set('Accept', 'application/json');

    // For JSON requests (POST/PUT/PATCH), ensure Content-Type is set
    const method = (clientOptions.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'PATCH'].includes(method) && !clientOptions.headers.get('Content-Type')) {
      clientOptions.headers.set('Content-Type', 'application/json');
    }

    // Auth token
    const token = localStorage.getItem('viBBes_token');
    if (token) {
      // ✅ Backend reads: req.headers['x-access-token'] OR req.headers.authorization
      clientOptions.headers.set('x-access-token', token);
      clientOptions.headers.set('Authorization', token); // token brut (pas "Bearer ")
    }

    return fetchUtils.fetchJson(url, clientOptions);
  };

  return {
    getList: (resource, params) => {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify(params.filter),
      };
      const url = `${apiUrl}/${resource}?${stringify(query)}`;

      return httpClient(url).then(({ headers, json }) => ({
        data: json,
        total: parseInt(headers.get('X-Total-Count')),
      }));
    },

    getOne: (resource, params) =>
      httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
        data: json,
      })),

    getMany: (resource, params) => {
      const query = { filter: JSON.stringify({ id: params.ids }) };
      const url = `${apiUrl}/${resource}?${stringify(query)}`;
      return httpClient(url).then(({ json }) => ({ data: json }));
    },

    getManyReference: (resource, params) => {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify({
          ...params.filter,
          [params.target]: params.id,
        }),
      };
      const url = `${apiUrl}/${resource}?${stringify(query)}`;

      return httpClient(url).then(({ headers, json }) => ({
        data: json,
        total: parseInt(headers.get('content-range').split('/').pop(), 10),
      }));
    },

    update: async (resource, params) => {
      if (resource !== 'products' && resource !== 'artists' && resource !== 'categories') {
        const data = await normalizeParams(resource, params);
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }).then(({ json }) => ({ data: json }));
      }

      try {
        const { data } = params;
        return httpClient(`${apiUrl}/${resource}/${params.id}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }).then(({ json }) => ({
          data: { ...params.data, id: json.id },
        }));
      } catch (e) {
        console.log('Error', e);
        return Promise.reject(new HttpError('Internal Server Error', '500', { fileUploadError: true }));
      }
    },

    updateMany: (resource, params) => {
      const query = { filter: JSON.stringify({ id: params.ids }) };
      return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({ data: json }));
    },

    create: async (resource, params) => {
      if (resource !== 'products' && resource !== 'artists' && resource !== 'categories') {
        const data = await normalizeParams(resource, params);
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(data),
        }).then(({ json }) => ({
          data: { ...params.data, id: json.id },
        }));
      }

      try {
        const { data } = params;
        return httpClient(`${apiUrl}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(data),
        }).then(({ json }) => ({
          data: { ...params.data, id: json.id },
        }));
      } catch (e) {
        return Promise.reject(new HttpError('Internal Server Error', '500', { fileUploadError: true }));
      }
    },

    delete: (resource, params) =>
      httpClient(`${apiUrl}/${resource}/${params.id}`, { method: 'DELETE' }).then(({ json }) => ({
        data: json,
      })),

    deleteMany: (resource, params) => {
      const query = { filter: JSON.stringify({ id: params.ids }) };
      return httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
        method: 'DELETE',
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({ data: json }));
    },
  };
};
